/**
 * useCustomerDisplaySession — SSE hook for customer-facing display
 * Manages connection, reconnection, session recovery on refresh
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { accountHeaders, getAccount, signOutAccount } from '@/lib/pwaAccount';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Display phases
export const PHASE = {
  IDLE: 'idle',
  ORDER_REVIEW: 'order_review',
  TIP_SELECT: 'tip_select',
  PAYMENT_SELECT: 'payment_select',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  RECEIPT: 'receipt',
  CANCELED: 'canceled',
};

const SESSION_ENDED = 'Your session ended. Please sign in again.';

/**
 * The stream URL for this terminal. EventSource cannot send headers, so the
 * account token rides as ?token= (and the device's location as
 * ?restaurant_id=); the server checks it exactly like the Bearer header.
 */
function streamUrl(terminalId) {
  const account = getAccount();
  if (!account) return null;
  const q = new URLSearchParams({ token: account.token, restaurant_id: String(account.restaurant.id) });
  return `${API}/api/display/stream/${encodeURIComponent(terminalId)}?${q.toString()}`;
}

export default function useCustomerDisplaySession(terminalId) {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [connected, setConnected] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [tipAmount, setTipAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('');
  const [resultType, setResultType] = useState(null);
  const [resultMessage, setResultMessage] = useState('');
  const [receiptData, setReceiptData] = useState(null);
  const [qrPayUrl, setQrPayUrl] = useState('');

  const esRef = useRef(null);
  const reconRef = useRef(null);
  const idleTimerRef = useRef(null);
  const hasConnectedOnce = useRef(false);

  // Reset to idle
  const resetToIdle = useCallback(() => {
    setPhase(PHASE.IDLE);
    setCheckout(null);
    setTipAmount(0);
    setPaymentStatus('');
    setResultType(null);
    setResultMessage('');
    setReceiptData(null);
    setQrPayUrl('');
  }, []);

  // Recover session on reconnect/refresh
  const recoverSession = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/display/session/${encodeURIComponent(terminalId)}`, {
        headers: accountHeaders(),
      });
      if (res.status === 401) { signOutAccount(SESSION_ENDED); return; }
      if (!res.ok) return;
      const data = await res.json();
      if (data.active && data.session) {
        const s = data.session;
        setCheckout(s);
        setTipAmount(s.tip_amount || 0);
        if (s.qr_pay_url) setQrPayUrl(s.qr_pay_url);
        // Determine phase from status
        if (s.status === 'active') {
          setPhase(PHASE.ORDER_REVIEW);
        } else if (['processing', 'waiting_for_card', 'card_inserted', 'processing_payment'].includes(s.status)) {
          setPhase(PHASE.PROCESSING);
          setPaymentStatus(s.status);
        } else {
          setPhase(PHASE.ORDER_REVIEW);
        }
      }
    } catch {
      // No session to recover, stay idle
    }
  }, [terminalId]);

  // Notify POS of customer action
  const notifyPOS = useCallback(async (eventType, payload = {}) => {
    try {
      const res = await fetch(`${API}/api/display/customer-action`, {
        method: 'POST',
        headers: accountHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ terminal_id: terminalId, event: eventType, ...payload }),
      });
      if (res.status === 401) signOutAccount(SESSION_ENDED);
    } catch { /* best-effort */ }
  }, [terminalId]);

  // SSE connection
  const connect = useCallback(() => {
    esRef.current?.close();
    clearTimeout(reconRef.current);

    const url = streamUrl(terminalId);
    if (!url) { setConnected(false); return; }
    const es = new EventSource(url);
    esRef.current = es;

    const handle = (type, raw) => {
      let d;
      try { d = JSON.parse(raw); } catch { return; }

      switch (type) {
        case 'connected':
          setConnected(true);
          // Only recover session on initial connect or if we're in idle (not during active checkout)
          if (!hasConnectedOnce.current) {
            hasConnectedOnce.current = true;
            recoverSession();
          }
          break;

        case 'CHECKOUT_START':
          clearTimeout(idleTimerRef.current);
          setCheckout(d);
          setTipAmount(0);
          setPaymentStatus('');
          setResultType(null);
          setReceiptData(null);
          setQrPayUrl(d.qr_pay_url || '');
          setPhase(PHASE.ORDER_REVIEW);
          break;

        case 'CHECKOUT_UPDATE':
          setPaymentStatus(d.status || '');
          if (d.qr_pay_url) setQrPayUrl(d.qr_pay_url);
          if (['processing', 'waiting_for_card', 'card_inserted', 'processing_payment'].includes(d.status)) {
            setPhase(PHASE.PROCESSING);
          }
          break;

        case 'CHECKOUT_END':
          setResultType(d.result);
          setResultMessage(d.message || '');
          setReceiptData(d.receipt_data || null);
          setPhase(PHASE.COMPLETE);
          break;

        case 'TIP_SELECTED':
          if (d.tip_amount !== undefined) setTipAmount(d.tip_amount);
          break;

        case 'PAYMENT_METHOD_SELECTED':
          break;

        case 'ping':
          break;
        default:
          break;
      }
    };

    ['connected', 'CHECKOUT_START', 'CHECKOUT_UPDATE', 'CHECKOUT_END', 'TIP_SELECTED', 'PAYMENT_METHOD_SELECTED', 'ping'].forEach(t =>
      es.addEventListener(t, (e) => handle(t, e.data))
    );
    es.onopen = () => setConnected(true);
    es.onerror = () => {
      setConnected(false);
      es.close();
      // EventSource hides the status: ask once whether the sign-in still holds
      // (a 401 signs the device out instead of retrying forever).
      fetch(`${API}/api/display/session/${encodeURIComponent(terminalId)}`, { headers: accountHeaders() })
        .then((r) => {
          if (r.status === 401) signOutAccount(SESSION_ENDED);
          else reconRef.current = setTimeout(connect, 3000);
        })
        .catch(() => { reconRef.current = setTimeout(connect, 3000); });
    };
  }, [terminalId, recoverSession]);

  useEffect(() => {
    connect();
    return () => {
      esRef.current?.close();
      clearTimeout(reconRef.current);
      clearTimeout(idleTimerRef.current);
    };
  }, [connect]);

  // Auto-return to idle after complete/receipt with delay
  const scheduleIdle = useCallback((delay = 8000) => {
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(resetToIdle, delay);
  }, [resetToIdle]);

  return {
    phase, setPhase,
    connected,
    checkout,
    tipAmount, setTipAmount,
    paymentStatus,
    resultType, resultMessage, receiptData,
    qrPayUrl,
    notifyPOS,
    resetToIdle,
    scheduleIdle,
    PHASE,
  };
}
